import React from 'react';
import SalesOverviewService from '../services/SalesOverviewService';
import { CHART_COLORS, CHART_SEMANTIC } from '../../../utils/chartColors';

export interface RevenueDonutProps {
  acceptedTotal: number;
  invoicedAgainst: number;
  remaining: number;
}

interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export default function RevenueDonutChart({ acceptedTotal, invoicedAgainst, remaining }: RevenueDonutProps): React.JSX.Element {
  const segments: Segment[] = [
    { key: 'invoiced', label: 'Invoiced (accepted jobs)', value: invoicedAgainst, color: CHART_COLORS.pink },
    { key: 'remaining', label: 'Accepted, not invoiced', value: remaining, color: CHART_COLORS.navy },
  ];

  const total = acceptedTotal;
  const radius = 80;
  const strokeWidth = 24;
  const centerX = 100;
  const centerY = 100;

  let currentAngle = 180;

  const arcs = segments.map((segment) => {
    const percentage = total > 0 ? segment.value / total : 0;
    const angle = percentage * 180;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const pathD =
      angle > 0
        ? `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`
        : '';

    return {
      ...segment,
      pathD,
      percentage: percentage * 100,
    };
  });

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
      }}
    >
      <div style={{ fontSize: '16px', fontWeight: 600, color: '#021a4a', marginBottom: '16px' }}>
        Invoiced vs Accepted (not invoiced)
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        {/* Half-circle chart */}
        <div style={{ position: 'relative', width: '200px', height: '110px' }}>
          <svg
            width="200"
            height="110"
            viewBox="0 0 200 110"
            style={{ overflow: 'visible' }}
          >
            {/* Background arc */}
            <path
              d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
              fill="none"
              stroke="#f1f5f9"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
            {/* Segment arcs */}
            {arcs.map(
              (arc) =>
                arc.pathD && (
                  <path
                    key={arc.key}
                    d={arc.pathD}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="butt"
                  />
                )
            )}
          </svg>
          {/* Center text */}
          <div
            style={{
              position: 'absolute',
              bottom: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#021a4a' }}>
              {SalesOverviewService.formatCurrency(total)}
            </div>
            <div style={{ fontSize: '10px', color: '#666' }}>Total accepted</div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
          {segments.map((segment) => (
            <div key={segment.key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '3px',
                  background: segment.color,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: '#666' }}>{segment.label}</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>
                  {SalesOverviewService.formatCurrency(segment.value)}
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#888', fontWeight: 500 }}>
                {total > 0 ? ((segment.value / total) * 100).toFixed(0) : 0}%
              </div>
            </div>
          ))}

          {/* Summary line */}
          <div
            style={{
              paddingTop: '8px',
              borderTop: '1px solid #e5e7eb',
              fontSize: '11px',
              color: '#666',
            }}
          >
            Invoiced: {SalesOverviewService.formatCurrency(invoicedAgainst)} · Remaining: {SalesOverviewService.formatCurrency(remaining)}
          </div>
        </div>
      </div>
    </div>
  );
}
